import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CasoFilter, CasoSort, CasoView, CasoFolder } from '@/types/casos';
import { CasoHeader } from '@/components/casos/CasoHeader';
import { FolderCard } from '@/components/casos/FolderCard';
import { FolderBreadcrumb } from '@/components/casos/FolderBreadcrumb';
import { CreateFolderDialog } from '@/components/casos/CreateFolderDialog';
import { EditFolderDialog } from '@/components/casos/EditFolderDialog';
import { DraggableCasoCard } from '@/components/casos/DraggableCasoCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useCasos } from '@/hooks/useCasos';
import { useCasoFolders, useMoveCaso, useUpdateCasoFolder } from '@/hooks/useCasoFolders';

export default function Casos() {
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get('folder');

  const [filter, setFilter] = useState<CasoFilter>('all');
  const [sort, setSort] = useState<CasoSort>('recent');
  const [view, setView] = useState<CasoView>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<CasoFolder | null>(null);

  const { data: folders = [], isLoading: isLoadingFolders } = useCasoFolders(folderId);
  const { data: casos = [], isLoading: isLoadingCasos } = useCasos(filter, sort, folderId, searchQuery);
  const { mutate: moveCaso } = useMoveCaso();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const filteredFolders = useMemo(() => {
    if (!searchQuery.trim()) return folders;
    const query = searchQuery.toLowerCase();
    return folders.filter(folder =>
      folder.name.toLowerCase().includes(query)
    );
  }, [folders, searchQuery]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const draggedItem = active.data.current;
    const dropTarget = over.data.current;

    if (draggedItem?.type === 'caso') {
      const targetFolderId = dropTarget?.type === 'folder' ? dropTarget.folder.id : null;
      moveCaso({ caso_id: draggedItem.caso.id, folder_id: targetFolderId });
    }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="container mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Casos / Clientes</h1>
          <p className="text-muted-foreground">
            Gerencie seus casos jurídicos com documentos e fontes organizadas.
          </p>
        </div>

        <FolderBreadcrumb />

        <CasoHeader
          filter={filter}
          onFilterChange={setFilter}
          sort={sort}
          onSortChange={setSort}
          view={view}
          onViewChange={setView}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onCreateFolder={() => setCreateFolderOpen(true)}
          currentFolderId={folderId}
        />

        {/* Pastas primeiro */}
        {isLoadingFolders ? (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-muted-foreground">Pastas</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center gap-3 mb-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          </div>
        ) : filteredFolders.length > 0 ? (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-muted-foreground">Pastas</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredFolders.map((folder) => (
                <FolderCard
                  key={folder.id}
                  folder={folder}
                  onEdit={setEditingFolder}
                />
              ))}
            </div>
          </div>
        ) : null}

        {/* Casos depois */}
        {isLoadingCasos ? (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-muted-foreground">Casos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="overflow-hidden rounded-lg border bg-card">
                  <Skeleton className="h-32 w-full" />
                  <div className="p-4 pt-8 space-y-2">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                    <div className="flex justify-between pt-2">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : casos.length > 0 ? (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-muted-foreground">Casos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {casos.map((caso) => (
                <DraggableCasoCard key={caso.id} caso={caso} />
              ))}
            </div>
          </div>
        ) : searchQuery ? (
          <div className="text-center py-12 text-muted-foreground">
            Nenhum caso encontrado
          </div>
        ) : null}

        <CreateFolderDialog
          open={createFolderOpen}
          onOpenChange={setCreateFolderOpen}
          parentFolderId={folderId}
        />
        
        <EditFolderDialog
          open={!!editingFolder}
          onOpenChange={(open) => !open && setEditingFolder(null)}
          folder={editingFolder}
        />
      </div>
    </DndContext>
  );
}
